import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ServiceRequestService } from '../../core/api/service-request.service';
import { ServiceRequest } from '../../shared/models/service-request';
import { statusTone } from '../../shared/status-tone';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { StatusLabelPipe } from '../../shared/status-label.pipe';
import { MneDatePipe } from '../../shared/mne-date.pipe';

@Component({
  standalone: true,
  imports: [RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe, StatusLabelPipe, MneDatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'sr.detailTitle' | t }}</h1>
      </header>
      @if (item(); as a) {
        <section class="detail-card">
          <div class="detail-heading">
            <div>
              <p class="eyebrow">#{{ a.ServiceRequestNumber ?? a.Id }}</p>
              <h2>{{ a.CustomerName || ('sr.fallback' | t) }}</h2>
            </div>
            <span class="status-pill" [class]="tone(a.StatusName)">{{ (a.StatusName | statusLabel) || '—' }}</span>
          </div>
          <dl>
            <dt>{{ 'sr.type' | t }}</dt><dd>{{ a.ServiceRequestTypeName || '—' }}</dd>
            <dt>{{ 'wo.manipulation' | t }}</dt><dd>{{ a.ManipulationName || '—' }}</dd>
            <dt>{{ 'sr.vessel' | t }}</dt><dd>{{ a.VesselName || '—' }}</dd>
            <dt>{{ 'va.organizationUnit' | t }}</dt><dd>{{ a.OrganizationUnitName || '—' }}</dd>
            <dt>{{ 'common.warehouse' | t }}</dt><dd>{{ a.WarehouseName || '—' }}</dd>
            <dt>{{ 'sr.dischargeCondition' | t }}</dt><dd>{{ a.DischargeConditionName || '—' }}</dd>
            <dt>{{ 'sr.payer' | t }}</dt><dd>{{ a.PayerName || '—' }}</dd>
            <dt>{{ 'sr.importerExporter' | t }}</dt><dd>{{ a.ImporterExporterName || '—' }}</dd>
            <dt>{{ 'wo.year' | t }}</dt><dd>{{ a.Year ?? '—' }}</dd>
            <dt>{{ 'common.createdBy' | t }}</dt><dd>{{ a.CreatedByName || '—' }}</dd>
            <dt>{{ 'sr.createdDate' | t }}</dt><dd>{{ a.CreatedDate | mneDate }}</dd>
          </dl>

          @if (a.Rows?.length) {
            <h3 class="section-title">{{ 'wo.rows' | t }}</h3>
            <section class="list">
              @for (row of a.Rows; track row.Id) {
                <div class="list-row static">
                  <div>
                    <strong>{{ row.CargoTypeName || (('wo.rowFallback' | t) + ' #' + row.Id) }}</strong>
                    <span>{{ row.PackagingTypeName || '' }}</span>
                  </div>
                  <div class="row-meta">
                    <span>{{ row.Quantity ?? '—' }}</span>
                  </div>
                </div>
              }
            </section>
          }
        </section>
        @if (error()) { <p class="error notice">{{ error() | t }}</p> }
      } @else if (error()) { <p class="error notice">{{ error() | t }}</p> } @else { <div class="skeleton-detail">
        <div class="skeleton-heading"></div>
        <div class="skeleton-line w-70"></div>
        <div class="skeleton-line w-55"></div>
        <div class="skeleton-line w-85"></div>
        <div class="skeleton-line w-40"></div>
      </div> }
    </main>
  `
})
export class ServiceRequestDetailPage implements OnInit {
  readonly item = signal<ServiceRequest | null>(null);
  readonly error = signal('');

  constructor(private readonly route: ActivatedRoute, private readonly api: ServiceRequestService) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!id) { this.error.set('sr.missingId'); return; }
    this.api.get(id).subscribe({
      next: item => this.item.set(item),
      error: err => this.error.set(err?.error?.Message ?? err?.error?.message ?? 'sr.detailLoadError')
    });
  }
  tone(status?: string | null): string { return statusTone(status); }
}
